import { RestError } from '@azure/core-http';
import { Grid, Link } from '@mui/material';
import { useAuth } from '@workos-inc/authkit-react';
import { useEffect, useState } from 'react';
import { parseErrorMessage } from '../../core/api/api.context';
import { useEnvironment } from '../../core/env/environment.context';
import { useHideSplashScreenEffect } from '../../hooks/use-hide-splash-screen.hook';
import { useMutationAcceptInvitation } from './invitations.queries';
import { useInviteLandingStyles } from './invite-landing.styles';

export const InviteLandingPage = () => {
  const { mutateAsync } = useMutationAcceptInvitation();
  const { isLoading, signIn } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function acceptInvite() {
      const urlParams = new URLSearchParams(window.location.search);
      const inviteToken = urlParams.get('invitation_token') || '';
      const email = urlParams.get('email') || '';
      const decodedEmail = decodeURIComponent(email);
      if (inviteToken) {
        mutateAsync(inviteToken, {
          onSuccess(result) {
            if (result.result?.success || result.result?.redirectToSignIn) {
              signIn({
                loginHint: decodedEmail || undefined
              });
            } else {
              setError(result.error || 'Failed to accept invitation');
            }
          },
          onError(error, variables, context) {
            console.error('Error accepting invitation', error, variables, context);
            setError(parseErrorMessage((error as RestError)?.message));
          }
        });
      }
    }

    if (!isLoading) {
      acceptInvite();
    }
  }, [isLoading, mutateAsync, signIn]);

  const classes = useInviteLandingStyles();
  const environment = useEnvironment();

  // Only if there is an error will we hide the splash screen and display the page.
  // Otherwise we will accept the invitation in the background and redirect to the sign in page.
  useHideSplashScreenEffect(!!error);
  if (!error) return null;

  const currentYear = new Date().getFullYear();

  return (
    <>
      <Grid container className={classes.root}>
        <Grid className={classes.InviteCard} size={6}>
          <Grid container className={classes.root} sx={{ flexDirection: 'column' }}>
            <Grid className={classes.MaxWidth} size={2}>
              <img alt="Logo" style={{ width: '288px' }} src={`/media/brands/${environment.brandId}/logo-full.png`} />
            </Grid>

            <Grid className={classes.MaxWidth}>
              <h1 className={classes.Heading}>Something went wrong</h1>
              <p>{`${error}.`}</p>
              <p>Please contact {environment.brand?.name} support for assistance and quote the above error.</p>
            </Grid>
          </Grid>
          <Grid className={classes.Footer}>
            &copy; {environment.brand?.name} {currentYear}
            <br />
            {environment.brand?.privacyUrl && (
              <Link style={{ paddingRight: '1em' }} href={environment.brand?.privacyUrl}>
                Privacy Policy
              </Link>
            )}
            {environment.brand?.contactUrl && (
              <Link style={{ padding: '0 1em' }} href={environment.brand?.contactUrl}>
                Contact Us
              </Link>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
